/**
 * Assemble a RuleContext from loaded master rows + a deal + its line items.
 * Shared by NewDealPage (live preview) and DealWorkspacePage (real-time gate).
 * Owned by B4 (src/pages) — a UI helper, NOT a shared lib (CONTRACT §2).
 */

import type {
  Country,
  Deal,
  DealLineItem,
  DealParty,
  DocumentRecord,
  Incoterm,
  Partner,
  PaymentTerm,
  Product,
  RuleContext,
  ShipperProfile,
} from "@/types";

export function buildRuleContext(args: {
  deal: Deal;
  lines: DealLineItem[];
  country: Country | null;
  incoterm: Incoterm | null;
  partners: Partner[];
  products: Product[];
  /** v2 — generalised per-deal parties (authorized_agent / lar / ...). */
  dealParties?: DealParty[];
  /** v2 — issued/attached documents (doc-missing + CO staleness rules). */
  documents?: DocumentRecord[];
  shipper?: ShipperProfile | null;
  paymentTerm?: PaymentTerm | null;
  /** v2 — "YYYY-MM-DD"; pass to enable L/C date rules (engine stays clock-free). */
  today?: string;
}): RuleContext {
  const {
    deal,
    lines,
    country,
    incoterm,
    partners,
    products,
    dealParties,
    documents,
    shipper,
    paymentTerm,
    today,
  } = args;
  const byId = new Map(partners.map((p) => [p.id, p]));

  const parties: Record<string, Partner | null> = {};
  for (const dp of dealParties ?? []) {
    parties[dp.role] = byId.get(dp.partner_id) ?? null;
  }

  // Products referenced by the deal's line items (for US regulatory rules).
  const referencedProductIds = new Set(
    lines.map((l) => l.product_id).filter((id): id is string => !!id),
  );
  const referencedProducts = products.filter((p) =>
    referencedProductIds.has(p.id),
  );

  return {
    deal,
    lines,
    partners: {
      buyer: deal.buyer_id ? byId.get(deal.buyer_id) ?? null : null,
      consignee: deal.consignee_id ? byId.get(deal.consignee_id) ?? null : null,
      notify: deal.notify_id ? byId.get(deal.notify_id) ?? null : null,
      importer_of_record: deal.importer_of_record_id
        ? byId.get(deal.importer_of_record_id) ?? null
        : null,
      fda_initial_importer: deal.fda_initial_importer_id
        ? byId.get(deal.fda_initial_importer_id) ?? null
        : null,
    },
    incoterm,
    country:
      country ??
      // Minimal shell so the engine never dereferences null country.
      ({
        id: deal.destination_country,
        name_en: deal.destination_country,
        name_ko: null,
        default_currency: deal.currency,
        required_documents: [],
        recommended_incoterms: [],
        cert_requirements: [],
        regulatory_notes: null,
        tariff_note: null,
        tariff_verified_at: null,
        tariff_source_url: null,
        required_importer_roles: [],
        is_sanctioned: false,
        language: "en",
        created_at: deal.created_at,
        updated_at: deal.updated_at,
      } satisfies Country),
    products: referencedProducts,
    documents: documents ?? [],
    parties,
    shipper: shipper ?? null,
    paymentTerm: paymentTerm ?? null,
    today,
  };
}
